import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default content values (fallbacks when database is empty)
const defaultContent = {
  hero: {
    tagline: 'Your Journey to Freedom Starts Here',
    title_line1: 'Transform Your Life,',
    title_line2: 'Embrace Freedom',
    subtitle: 'Discover the path to financial peace, mental clarity, and physical vitality. Our holistic coaching empowers you to break free from stress and live life on your terms.',
    cta_primary: 'Start Your Journey',
    cta_secondary: 'Watch Introduction',
  },
  stats: {
    stat1_value: '10K+', stat1_label: 'Lives Transformed',
    stat2_value: '50+', stat2_label: 'Countries Reached',
    stat3_value: '98%', stat3_label: 'Client Satisfaction',
    stat4_value: '500+', stat4_label: 'Coaching Hours',
  },
  freedoms: {
    section_title: 'Your Path to Complete Freedom',
    section_subtitle: 'We guide you through three interconnected pillars of wellness that together create lasting transformation.',
    financial_title: 'Financial Freedom',
    financial_description: 'Break free from financial stress. Learn strategies to build wealth, eliminate debt, and create sustainable income streams that work for you.',
    financial_feature1: 'Debt elimination strategies',
    financial_feature2: 'Passive income building',
    financial_feature3: 'Smart investment basics',
    mental_title: 'Mental Freedom',
    mental_description: 'Quiet the noise. Discover mindfulness techniques, stress management tools, and cognitive practices that bring clarity and peace.',
    mental_feature1: 'Mindfulness practices',
    mental_feature2: 'Stress management',
    mental_feature3: 'Emotional resilience',
    physical_title: 'Physical Freedom',
    physical_description: 'Move without limits. Develop fitness routines, nutrition habits, and lifestyle changes that empower your body to thrive anywhere.',
    physical_feature1: 'Flexible fitness routines',
    physical_feature2: 'Nutrition guidance',
    physical_feature3: 'Location independence',
  },
  testimonials: {
    section_title: 'Stories of Transformation',
    section_subtitle: 'Hear from people who have embraced freedom and transformed their lives.',
    testimonial1_name: 'Sarah Mitchell',
    testimonial1_role: 'Digital Nomad',
    testimonial1_content: 'Nspire Living Healthy transformed my relationship with money and stress. I now work from anywhere in the world while feeling financially secure and mentally at peace.',
    testimonial2_name: 'Marcus Chen',
    testimonial2_role: 'Entrepreneur',
    testimonial2_content: 'The holistic approach here is unmatched. I\'ve achieved a balance I never thought possible - thriving business, clear mind, and energy to enjoy life.',
  },
  cta: {
    title: 'Ready to Transform Your Life?',
    subtitle: 'Join thousands who have found freedom through our holistic approach to wellness.',
    button_text: 'Begin Your Transformation',
  },
  coaching: {
    page_title: 'Personal Coaching Sessions',
    page_subtitle: 'Book one-on-one sessions with our expert coaches to accelerate your journey to freedom.',
    phone_title: 'Phone Call',
    phone_description: 'One-on-one phone coaching session',
    chat_title: 'Chat Session',
    chat_description: 'Text-based coaching conversation',
    video_title: 'Video Call',
    video_description: 'Face-to-face video coaching',
    booking_cta: 'Book Your Session',
  },
  media: {
    page_title: 'Media Library',
    page_subtitle: 'Explore our collection of videos, lectures, and resources to support your journey.',
    empty_state_title: 'No media available yet',
    empty_state_text: 'Check back soon for new content!',
  },
  apps: {
    page_title: 'Our Apps',
    page_subtitle: 'Download our apps to continue your journey to freedom wherever you go.',
    empty_state_title: 'No apps available yet',
    empty_state_text: 'Stay tuned for our upcoming releases!',
  },
  audio: {
    page_title: 'Audio Library',
    page_subtitle: 'Listen to meditations, affirmations, relaxing sounds, and more to support your wellness journey.',
    empty_state_title: 'No audio found',
    empty_state_text: 'Check back later for new content',
  },
  footer: {
    tagline: 'Transform your life through holistic coaching. Achieve financial, mental, and physical freedom.',
    email: 'hello@nspireliving.com',
    phone: '+1 (234) 567-890',
    location: 'Anywhere, Everywhere',
    copyright_text: 'Nspire Living Healthy. All rights reserved.',
    made_with_text: 'Made with love for a healthier world',
  },
  general: {
    site_name: 'Nspire Living Healthy',
    site_description: 'A lifestyle coaching platform for financial, mental, and physical freedom',
    logo_text: 'N',
  },
};

const SiteContentContext = createContext();

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      const fetchedContent = response.data || {};
      
      // Merge fetched content with defaults (fetched values override defaults)
      const mergedContent = { ...defaultContent };
      for (const section of Object.keys(defaultContent)) {
        mergedContent[section] = {
          ...defaultContent[section],
          ...(fetchedContent[section] || {}),
        };
      }
      
      setContent(mergedContent);
      setError(null);
    } catch (err) {
      console.error('Error fetching site content:', err);
      setError(err);
      // Keep using default content on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  // Helper function to get content for a specific section
  const getSection = (section) => {
    return content[section] || defaultContent[section] || {};
  };

  // Helper function to get a specific content value
  const getValue = (section, key, fallback = '') => {
    return content[section]?.[key] || defaultContent[section]?.[key] || fallback;
  };

  // Refresh content (useful after admin saves)
  const refreshContent = () => {
    setLoading(true);
    fetchContent();
  };

  return (
    <SiteContentContext.Provider value={{
      content,
      loading,
      error,
      getSection,
      getValue,
      refreshContent,
    }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContent must be used within a SiteContentProvider');
  }
  return context;
};

export default SiteContentContext;
